import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  // Requests fired from the /admin area use the ISOLATED admin token so a
  // concurrent student/competition login (stored under the shared "token"
  // key) can never be sent to an admin endpoint. Everywhere else keeps the
  // existing player/elim session behaviour.
  const path = (typeof window !== "undefined" && window.location.pathname) || "";
  const token = path.startsWith("/admin")
    ? localStorage.getItem("admin_token")
    : (sessionStorage.getItem("token") || localStorage.getItem("token"));
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Global response interceptor: when a 401 hits an admin endpoint, the stored
// token is stale (expired or invalidated). Wipe it, surface a toast so the
// user understands what happened, and bounce them to the admin login page.
// Public endpoints (no token attached) returning 401 are left alone — the
// caller decides what to do.
//
// Re-entry guard via a module-level flag stops a burst of 401s (typical when
// a page mounts several admin queries at once) from spamming toasts AND from
// triggering multiple navigations.
let _sessionExpiredHandled = false;
// Any endpoint that requires ADMIN authentication. When a request against
// one of these returns 401 (Missing / Invalid / Expired token) the stored
// admin token is stale and we must bounce the user to /admin/login instead
// of surfacing a raw "Missing token" toast (which is confusing — users
// think it's a bug, not an expired session).
const ADMIN_URL_PATTERNS = [
  /^\/admin\//,
  /^\/homepage-config(\/|$|\?)/,
  /^\/footer(\/|$|\?)/,
  /^\/games\/[^/]+$/,          // PUT /games/{id}
  /^\/games$/,                 // POST/PUT /games (bulk)
  /^\/questions/,
  /^\/credentials/,
  /^\/eliminatorias\/(users|create|import|bulk-delete)/,
  /^\/robots/,
  /^\/rankings\/admin/,
  /^\/inscriptions\/admin/,
  /^\/vs-human\/admin/,
  /^\/vs-robot\/admin/,
];
function isAdminUrl(url) {
  return ADMIN_URL_PATTERNS.some((re) => re.test(url || ""));
}
// Login endpoints. A 401 here means "wrong credentials" (an authentication
// failure the caller already surfaces inline) — NOT an expired session. These
// must never trigger the session-expired redirect to /admin/login.
function isLoginUrl(url) {
  return /^\/auth\/[^/]+\/login(\/|$|\?)/.test(url || "");
}
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const method = (err?.config?.method || "get").toLowerCase();
    const hadToken = !!(sessionStorage.getItem("token") || localStorage.getItem("token") || localStorage.getItem("admin_token"));
    // A 401 on a known ADMIN endpoint means the session is stale — even if
    // the client-side token slot is empty (backend returned "Missing token"
    // because the interceptor didn't have a token to attach). In BOTH cases
    // we send the user back to /admin/login with a clear toast, so they can
    // resume the same task in a fresh session.
    const looksLikeAdmin = isAdminUrl(url) || (
      // Fallback: any non-GET admin-ish request that gets a 401 is treated
      // as expired admin session, even for URLs not in the pattern list.
      status === 401 && hadToken && method !== "get"
    );
    if (status === 401 && looksLikeAdmin && !isLoginUrl(url)) {
      sessionStorage.removeItem("token");
      localStorage.removeItem("token");
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_role");
      localStorage.removeItem("admin_extra");
      if (typeof window !== "undefined" && !_sessionExpiredHandled) {
        _sessionExpiredHandled = true;
        try {
          sessionStorage.setItem("_session_expired", "1");
        } catch (_) { /* ignore quota */ }
        if (!window.location.pathname.endsWith("/admin/login")) {
          window.location.assign("/admin/login");
        } else {
          try {
            toast.error("A tua sessão expirou — volta a entrar.", {
              id: "session-expired",
              duration: 5000,
            });
          } catch (_) { /* sonner not mounted yet */ }
        }
      }
    }
    return Promise.reject(err);
  },
);
